import React, { useEffect, useMemo, useState } from "react";
import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Grid,
  Group,
  NumberInput,
  Paper,
  Popover,
  ScrollArea,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  UnstyledButton,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { IconCircleCheck, IconExclamationMark, IconInfoCircle } from "@tabler/icons";
import { useTranslation } from "react-i18next";
import { Page, FormSectionCard } from "../../layout/page";
import PageListHeader from "../../components/pageListHeader";
import { useServerApi } from "../../hooks/userServerApi";
import { useTabNavigate } from "../../hooks/useTabNavigate";
import { buildCodeSample } from "../../helpers/codeSample";

const SOURCE_COLOR = {
  file: "blue",
  builtin: "gray",
  saved: "green",
  derived: "yellow",
  missing: "red",
};

const SOURCE_LABEL = {
  file: "Server config",
  builtin: "Built-in",
  saved: "Saved in settings",
  derived: "Derived",
  missing: "Missing",
};

const PART_COLOR = {
  prefix: "blue",
  date: "teal",
  sep: "gray",
  prefixSep: "gray",
  seq: "orange",
  initials: "blue",
  provider: "blue",
  parent: "violet",
};

const FIELD_TIPS = {
  strategy:
    "Fixed prefix uses one prefix for every person. Prefix by user type uses a different prefix for staff, back office, consultant, and channel. Name initials uses the first letters of the name. Provider code uses the insurer code. Related record hangs the number off another record.",
  method:
    "SEQUENCE keeps one running number for this prefix. DATE_SEQUENCE restarts when the date text changes, such as a new month. RELATION_SEQUENCE numbers under another record's code.",
  prefix: "Characters at the start of every code. Client codes use C, so the first number looks like C000001.",
  counterPrefix:
    "This text is stored on the counter, not always in the visible code. Quiz stores the counter as quiz-2026 and prints the code as QZ-2026-001.",
  prefixSeparator: "Characters placed between the optional prefix and the related record's code.",
  numberSeperator:
    "Characters between the front of the code and the sequence. Leave this blank for none. A hyphen turns C000001 into C-000001.",
  numberPad: "How many digits the sequence uses. Pad 6 with sequence 1 is 000001.",
  namePad: "How many digits follow the initials. Pad 3 turns AB into AB001.",
  dateFmt:
    "Which part of the date is written into the code. YYMM for 15 March 2026 is 2603. The sequence starts again when this text changes.",
  dateSource: "Where the date comes from. Bank transactions use the transaction date. Other codes use today.",
  codeType:
    "Keeps this sequence separate when another code uses the same prefix. Changing it starts a new counter.",
  counterType:
    "The name of the counter document. Staff type and name codes use STAFFID. Product codes use PRODUCT.",
  relatedModel: "The record whose code is copied into this number, such as the product category.",
  relatedField: "The field on this record that points at the related record.",
  userTypePrefixes:
    "The prefix used for each user type. Consultant T with pad 4 produces T0001. Default is used when the type is not one of the four.",
};

function fieldsForDraft(item, rule) {
  if (!item?.editable) return [];
  if (item.sourceKind === "user") {
    const strategy = rule?.strategy || "prefix";
    if (strategy === "userType") {
      return ["strategy", "userTypePrefixes", "numberPad", "counterType"];
    }
    if (strategy === "name") return ["strategy", "namePad", "counterType"];
    const fields = ["strategy", "prefix", "numberSeperator", "numberPad", "method", "codeType"];
    if ((rule?.method || "SEQUENCE") === "DATE_SEQUENCE") fields.push("dateFmt");
    return fields;
  }
  if (item.sourceKind === "product") {
    if (rule?.strategy === "relation") {
      return [
        "strategy",
        "prefix",
        "prefixSeparator",
        "relatedModel",
        "relatedField",
        "numberPad",
        "numberSeperator",
        "codeType",
      ];
    }
    return ["strategy", "numberPad", "counterType"];
  }
  const method = rule?.method || "DATE_SEQUENCE";
  if (method === "RELATION_SEQUENCE") {
    return [
      "method",
      "prefix",
      "prefixSeparator",
      "relatedModel",
      "relatedField",
      "numberPad",
      "numberSeperator",
      "codeType",
    ];
  }
  const fields = ["method", "prefix", "numberSeperator", "numberPad", "codeType"];
  if (method === "DATE_SEQUENCE") {
    fields.push("dateFmt");
    if (item.dateSource === "field") fields.push("dateSource");
    if (rule?.counterPrefix || item.key === "quiz") fields.push("counterPrefix");
  }
  return fields;
}

function FieldTip({ text }) {
  const [opened, setOpened] = useState(false);
  return (
    <Popover
      opened={opened}
      onClose={() => setOpened(false)}
      position="bottom"
      withArrow
      width={280}
      shadow="md"
    >
      <Popover.Target>
        <ActionIcon
          size="xs"
          variant="subtle"
          color="gray"
          aria-label="Explain this field"
          onClick={() => setOpened((value) => !value)}
        >
          <IconInfoCircle size={14} />
        </ActionIcon>
      </Popover.Target>
      <Popover.Dropdown>
        <Text size="sm">{text}</Text>
      </Popover.Dropdown>
    </Popover>
  );
}

function Field({ label, tip, children }) {
  const { t } = useTranslation();
  return (
    <Box>
      <Group spacing={4} mb={4} align="center">
        <Text size="sm" weight={500}>
          {t(label)}
        </Text>
        {tip ? <FieldTip text={t(tip)} /> : null}
      </Group>
      {children}
    </Box>
  );
}

const CodeSetting = () => {
  const [api] = useServerApi();
  const { t } = useTranslation();
  const tabNav = useTabNavigate();
  const [rows, setRows] = useState([]);
  const [key, setKey] = useState("client");
  const [draft, setDraft] = useState(null);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const selected = rows.find((row) => row.key === key) || null;

  const applyRows = (list, preferred) => {
    const next = Array.isArray(list) ? list : [];
    setRows(next);
    const match = next.find((row) => row.key === (preferred || key)) || next[0];
    if (match) {
      setKey(match.key);
      setDraft(
        match.rule
          ? { ...match.rule, userTypePrefixes: { ...(match.rule.userTypePrefixes || {}) } }
          : {},
      );
    }
  };

  const load = async (preferred) => {
    try {
      setLoading(true);
      const result = await api.codeSetting.list();
      if (result?.success) applyRows(result.data, preferred);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load("client");
  }, []);

  const groups = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const filtered = rows.filter((row) => {
      if (!needle) return true;
      return (
        row.label.toLowerCase().includes(needle) ||
        String(row.group || "").toLowerCase().includes(needle)
      );
    });
    const order = [];
    const byGroup = new Map();
    filtered.forEach((row) => {
      const group = row.group || "Other";
      if (!byGroup.has(group)) {
        byGroup.set(group, []);
        order.push(group);
      }
      byGroup.get(group).push(row);
    });
    return order.map((group) => ({ group, items: byGroup.get(group) }));
  }, [rows, query]);

  const setField = (name, value) => {
    setDraft((prev) => ({ ...(prev || {}), [name]: value }));
  };

  const choose = (nextKey) => {
    const match = rows.find((row) => row.key === nextKey);
    setKey(nextKey);
    setDraft(
      match?.rule
        ? { ...match.rule, userTypePrefixes: { ...(match.rule.userTypePrefixes || {}) } }
        : {},
    );
  };

  const save = async () => {
    if (!selected || !draft) return;
    const methodChanged =
      (selected.rule?.method || "") !== (draft.method || "") ||
      (selected.rule?.strategy || "") !== (draft.strategy || "");
    const formatChanged = [
      "prefix",
      "dateFmt",
      "numberPad",
      "numberSeperator",
      "counterPrefix",
      "namePad",
    ].some((field) => String(selected.rule?.[field] ?? "") !== String(draft[field] ?? ""));
    if (methodChanged || formatChanged) {
      const ok = window.confirm(
        t(
          "Saving this code format starts a new counter bucket when the prefix, date format, or method changes. Existing numbers stay as they are. Continue?",
        ),
      );
      if (!ok) return;
    }
    try {
      setSaving(true);
      const result = await api.codeSetting.save(selected.key, {
        rule: draft,
        confirmed: methodChanged,
      });
      if (!result?.success) throw result;
      setRows((prev) => prev.map((row) => (row.key === selected.key ? result.data : row)));
      setDraft(result.data.rule);
      showNotification({
        title: t("Save"),
        color: "green",
        icon: <IconCircleCheck size={18} />,
        message: t("settings.saved"),
      });
    } catch (error) {
      showNotification({
        title: t("Save"),
        color: "red",
        icon: <IconExclamationMark size={18} />,
        message: error?.error || error?.message || t("settings.saveFailed"),
      });
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!selected) return;
    if (!window.confirm(t("Use the server config for this code again?"))) return;
    try {
      setSaving(true);
      const result = await api.codeSetting.reset(selected.key);
      if (!result?.success) throw result;
      setRows((prev) => prev.map((row) => (row.key === selected.key ? result.data : row)));
      setDraft(result.data.rule);
      showNotification({
        title: t("Auto Code"),
        color: "green",
        icon: <IconCircleCheck size={18} />,
        message: t("Server config"),
      });
    } catch (error) {
      showNotification({
        title: t("Auto Code"),
        color: "red",
        icon: <IconExclamationMark size={18} />,
        message: error?.error || error?.message || t("settings.saveFailed"),
      });
    } finally {
      setSaving(false);
    }
  };

  const fields = fieldsForDraft(selected, draft);
  const show = (name) => fields.includes(name);
  const sample = buildCodeSample(selected, draft || selected?.rule);
  const showFormat = ["prefix", "numberSeperator", "numberPad", "namePad", "prefixSeparator"].some(
    show,
  );
  const showDate = show("dateFmt") || show("dateSource");
  const showRelation = show("relatedModel") || show("relatedField");
  const showCounter = show("codeType") || show("counterType") || show("counterPrefix");

  return (
    <Page>
      <PageListHeader
        title={t("Auto Code")}
        actionButtons={[
          {
            label: t("Save"),
            onClick: save,
            loading: saving,
            disabled: loading || !selected?.editable,
          },
          {
            label: t("Server config"),
            onClick: reset,
            variant: "default",
            disabled: loading || selected?.source !== "saved",
          },
          {
            label: t("Close"),
            onClick: () => tabNav.closeActive(),
            variant: "default",
          },
        ]}
      />
      <Grid align="flex-start">
        <Grid.Col span={12} md={4}>
          <Paper withBorder p="sm" radius={0}>
            <TextInput
              mb="sm"
              placeholder={t("Search")}
              value={query}
              onChange={(event) => setQuery(event.currentTarget.value)}
            />
            <ScrollArea style={{ height: "calc(100vh - 220px)" }} offsetScrollbars>
              <Stack spacing="md">
                {groups.map(({ group, items }) => (
                  <Box key={group}>
                    <Text size="xs" weight={600} color="dimmed" transform="uppercase" mb={6}>
                      {group}
                    </Text>
                    <Stack spacing={4}>
                      {items.map((row) => {
                        const active = row.key === key;
                        return (
                          <UnstyledButton
                            key={row.key}
                            onClick={() => choose(row.key)}
                            sx={(theme) => ({
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 8,
                              padding: "8px 10px",
                              borderRadius: 4,
                              backgroundColor: active
                                ? theme.colorScheme === "dark"
                                  ? theme.colors.dark[5]
                                  : theme.colors.blue[0]
                                : "transparent",
                              borderLeft: active
                                ? `3px solid ${theme.colors.blue[6]}`
                                : "3px solid transparent",
                              "&:hover": {
                                backgroundColor:
                                  theme.colorScheme === "dark"
                                    ? theme.colors.dark[4]
                                    : theme.colors.gray[0],
                              },
                            })}
                          >
                            <Text size="sm" weight={active ? 600 : 400}>
                              {row.label}
                            </Text>
                            <Badge
                              size="xs"
                              color={SOURCE_COLOR[row.source] || "gray"}
                              variant="light"
                            >
                              {t(SOURCE_LABEL[row.source] || row.source)}
                            </Badge>
                          </UnstyledButton>
                        );
                      })}
                    </Stack>
                  </Box>
                ))}
                {!loading && groups.length === 0 && (
                  <Text size="sm" color="dimmed">
                    {t("No codes match this search.")}
                  </Text>
                )}
              </Stack>
            </ScrollArea>
          </Paper>
        </Grid.Col>
        <Grid.Col span={12} md={8}>
          {!selected && (
            <Text size="sm" color="dimmed">
              {loading ? t("Loading") : t("Select a code")}
            </Text>
          )}
          {selected && (
            <Stack spacing="md">
              <FormSectionCard title="Sample" paperPadding="md" mb={0}>
                <Stack spacing="sm">
                  <Group position="apart">
                    <Text size="sm" weight={600}>
                      {selected.label}
                    </Text>
                    <Badge color={SOURCE_COLOR[selected.source] || "gray"}>
                      {t(SOURCE_LABEL[selected.source] || selected.source)}
                    </Badge>
                  </Group>
                  <Text size="xs" color="dimmed">
                    {t(
                      "This sample uses the values on this page. Sequence 1 is shown. Nothing is reserved.",
                    )}
                  </Text>
                  <Paper withBorder p="md" radius="sm">
                    {sample.parts.length > 0 ? (
                      <Group spacing="lg" align="flex-end">
                        {sample.parts.map((part) => (
                          <Stack key={`${part.key}-${part.label}`} spacing={2}>
                            <Text
                              weight={700}
                              sx={(theme) => ({
                                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                                fontSize: 28,
                                lineHeight: 1,
                                color: theme.colors[PART_COLOR[part.key] || "dark"][7],
                              })}
                            >
                              {part.text}
                            </Text>
                            <Text size="xs" color="dimmed">
                              {t(part.label)}
                            </Text>
                          </Stack>
                        ))}
                      </Group>
                    ) : (
                      <Text
                        weight={700}
                        sx={{
                          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                          fontSize: 22,
                        }}
                      >
                        {sample.text || "—"}
                      </Text>
                    )}
                    <Text size="sm" mt="sm">
                      {t("Full sample")}: {sample.text || "—"}
                    </Text>
                  </Paper>
                  <SimpleGrid cols={2} spacing="sm">
                    <Box>
                      <Text size="xs" color="dimmed">
                        {t("Last saved code")}
                      </Text>
                      <Text size="sm" weight={600}>
                        {selected.lastCode || "—"}
                      </Text>
                    </Box>
                    <Box>
                      <Text size="xs" color="dimmed">
                        {t("Counter")}
                      </Text>
                      <Text size="sm" weight={600}>
                        {(selected.counters || []).length
                          ? selected.counters
                              .map(
                                (row) =>
                                  `${row.type ? `${row.type} ` : ""}${row.bucket} = ${row.seq ?? "—"}`,
                              )
                              .join(", ")
                          : "—"}
                      </Text>
                    </Box>
                  </SimpleGrid>
                  {selected.companyScoped && (
                    <Text size="xs" color="dimmed">
                      {t(
                        "Duplicate check includes the company. The counter itself stays global.",
                      )}
                    </Text>
                  )}
                  {selected.note && <Text size="sm">{selected.note}</Text>}
                  {selected.fileIgnored && (
                    <Alert color="yellow">
                      {t(
                        "The config file lists a different prefix. Issued codes follow the built-in pattern until you save a rule here.",
                      )}
                    </Alert>
                  )}
                </Stack>
              </FormSectionCard>

              {selected.editable && draft && (show("method") || show("strategy")) && (
                <FormSectionCard title="How the code is chosen" paperPadding="md" mb={0}>
                  <SimpleGrid cols={2} breakpoints={[{ maxWidth: "sm", cols: 1 }]}>
                    {show("strategy") && (
                      <Field label="Strategy" tip={FIELD_TIPS.strategy}>
                        <Select
                          value={draft.strategy || null}
                          onChange={(value) => setField("strategy", value)}
                          data={
                            selected.sourceKind === "product"
                              ? [
                                  { value: "provider", label: t("Provider code") },
                                  { value: "relation", label: t("Related record") },
                                ]
                              : [
                                  { value: "prefix", label: t("Fixed prefix") },
                                  { value: "userType", label: t("Prefix by user type") },
                                  { value: "name", label: t("Name initials") },
                                ]
                          }
                        />
                      </Field>
                    )}
                    {show("method") && (
                      <Field label="Method" tip={FIELD_TIPS.method}>
                        <Select
                          value={draft.method || null}
                          onChange={(value) => setField("method", value)}
                          data={(selected.allowedMethods || []).map((method) => ({
                            value: method,
                            label: method,
                          }))}
                        />
                      </Field>
                    )}
                  </SimpleGrid>
                </FormSectionCard>
              )}

              {selected.editable && draft && showFormat && (
                <FormSectionCard title="Code format" paperPadding="md" mb={0}>
                  <SimpleGrid cols={2} breakpoints={[{ maxWidth: "sm", cols: 1 }]}>
                    {show("prefix") && (
                      <Field label="Prefix" tip={FIELD_TIPS.prefix}>
                        <TextInput
                          value={draft.prefix ?? ""}
                          onChange={(event) => setField("prefix", event.currentTarget.value)}
                        />
                      </Field>
                    )}
                    {show("prefixSeparator") && (
                      <Field label="Prefix separator" tip={FIELD_TIPS.prefixSeparator}>
                        <TextInput
                          value={draft.prefixSeparator ?? ""}
                          onChange={(event) =>
                            setField("prefixSeparator", event.currentTarget.value)
                          }
                        />
                      </Field>
                    )}
                    {show("numberSeperator") && (
                      <Field label="Number separator" tip={FIELD_TIPS.numberSeperator}>
                        <TextInput
                          value={draft.numberSeperator ?? ""}
                          onChange={(event) =>
                            setField("numberSeperator", event.currentTarget.value)
                          }
                        />
                      </Field>
                    )}
                    {show("numberPad") && (
                      <Field label="Number pad" tip={FIELD_TIPS.numberPad}>
                        <NumberInput
                          min={1}
                          max={12}
                          value={draft.numberPad ?? 1}
                          onChange={(value) => setField("numberPad", value)}
                        />
                      </Field>
                    )}
                    {show("namePad") && (
                      <Field label="Number pad" tip={FIELD_TIPS.namePad}>
                        <NumberInput
                          min={1}
                          max={12}
                          value={draft.namePad ?? 3}
                          onChange={(value) => setField("namePad", value)}
                        />
                      </Field>
                    )}
                  </SimpleGrid>
                </FormSectionCard>
              )}

              {selected.editable && draft && showDate && (
                <FormSectionCard title="Date" paperPadding="md" mb={0}>
                  <SimpleGrid cols={2} breakpoints={[{ maxWidth: "sm", cols: 1 }]}>
                    {show("dateFmt") && (
                      <Field label="Date format" tip={FIELD_TIPS.dateFmt}>
                        <Select
                          value={draft.dateFmt || null}
                          onChange={(value) => setField("dateFmt", value)}
                          data={(selected.dateFormats || []).map((fmt) => ({
                            value: fmt,
                            label: fmt,
                          }))}
                        />
                      </Field>
                    )}
                    {show("dateSource") && (
                      <Field label="Date source" tip={FIELD_TIPS.dateSource}>
                        <TextInput value={selected.dateField || "date"} disabled />
                      </Field>
                    )}
                  </SimpleGrid>
                </FormSectionCard>
              )}

              {selected.editable && draft && showRelation && (
                <FormSectionCard title="Related record" paperPadding="md" mb={0}>
                  <SimpleGrid cols={2} breakpoints={[{ maxWidth: "sm", cols: 1 }]}>
                    {show("relatedModel") && (
                      <Field label="Related model" tip={FIELD_TIPS.relatedModel}>
                        <TextInput
                          value={draft.relatedModel ?? ""}
                          onChange={(event) => setField("relatedModel", event.currentTarget.value)}
                        />
                      </Field>
                    )}
                    {show("relatedField") && (
                      <Field label="Related field" tip={FIELD_TIPS.relatedField}>
                        <TextInput
                          value={draft.relatedField ?? ""}
                          onChange={(event) => setField("relatedField", event.currentTarget.value)}
                        />
                      </Field>
                    )}
                  </SimpleGrid>
                </FormSectionCard>
              )}

              {selected.editable && draft && showCounter && (
                <FormSectionCard title="Sequence counter" paperPadding="md" mb={0}>
                  <SimpleGrid cols={2} breakpoints={[{ maxWidth: "sm", cols: 1 }]}>
                    {show("codeType") && (
                      <Field label="Counter type" tip={FIELD_TIPS.codeType}>
                        <TextInput
                          value={draft.codeType ?? ""}
                          onChange={(event) => setField("codeType", event.currentTarget.value)}
                        />
                      </Field>
                    )}
                    {show("counterType") && (
                      <Field label="Counter type" tip={FIELD_TIPS.counterType}>
                        <TextInput value={draft.counterType || ""} disabled />
                      </Field>
                    )}
                    {show("counterPrefix") && (
                      <Field label="Counter prefix" tip={FIELD_TIPS.counterPrefix}>
                        <TextInput
                          value={draft.counterPrefix ?? ""}
                          onChange={(event) => setField("counterPrefix", event.currentTarget.value)}
                        />
                      </Field>
                    )}
                  </SimpleGrid>
                </FormSectionCard>
              )}

              {selected.editable && draft && show("userTypePrefixes") && (
                <FormSectionCard title="User type prefixes" paperPadding="md" mb={0}>
                  <Field label="User type prefixes" tip={FIELD_TIPS.userTypePrefixes}>
                    <SimpleGrid cols={2} breakpoints={[{ maxWidth: "sm", cols: 1 }]} mt="xs">
                      {["STAFF", "BACKOFFICE", "CONSULTANT", "CHANNEL", "default"].map((type) => (
                        <TextInput
                          key={type}
                          label={type}
                          value={draft.userTypePrefixes?.[type] ?? ""}
                          onChange={(event) =>
                            setField("userTypePrefixes", {
                              ...(draft.userTypePrefixes || {}),
                              [type]: event.currentTarget.value,
                            })
                          }
                        />
                      ))}
                    </SimpleGrid>
                  </Field>
                </FormSectionCard>
              )}
            </Stack>
          )}
        </Grid.Col>
      </Grid>
    </Page>
  );
};

export default CodeSetting;
